import { notify } from '@affine/component';
import type {
  AIDraftService,
  AIToolsConfigService,
} from '@affine/core/modules/ai-button';
import type { AIModelService } from '@affine/core/modules/ai-button/services/models';
import type {
  ServerService,
  SubscriptionService,
} from '@affine/core/modules/cloud';
import {
  WorkspaceApiError,
  workspaceApiFetch,
} from '@affine/core/modules/cloud';
import type { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import type { WorkspaceNavigationPort } from '@affine/core/modules/streamlain/copilot/workspace-navigation-executor';
import type { AppThemeService } from '@affine/core/modules/theme';
import type { WorkbenchService } from '@affine/core/modules/workbench';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import type {
  CopilotChatHistoryFragment,
  UpdateChatSessionInput,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { type NotificationService } from '@blocksuite/affine/shared/services';
import type { EditorHost } from '@blocksuite/affine/std';
import { ShadowlessElement } from '@blocksuite/affine/std';
import type { ExtensionType, Store } from '@blocksuite/affine/store';
import { type Signal, signal } from '@preact/signals-core';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { css, html, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';

import type { WorkspaceDocSyncEngine } from '../../../modules/streamlain/stream-objects';
import { AffineIcon } from '../_common/icons';
import type { ChatBlockTurns } from '../blocks/ai-chat-block/chat-block-turns';
import type { SearchMenuConfig } from '../components/ai-chat-add-context';
import type { DocDisplayConfig } from '../components/ai-chat-chips';
import type {
  ChatContextPickingControl,
  ChatContextValue,
  ExternalChatContextDoc,
  WorkflowContextAttachment,
  WorkspaceViewContextReader,
} from '../components/ai-chat-content';
import type {
  AINetworkSearchConfig,
  AIPlaygroundConfig,
  AIReasoningConfig,
} from '../components/ai-chat-input';
import type { ChatMessage } from '../components/ai-chat-messages';
import { AIProvider } from '../provider';
import type { MateOptionData } from './ai-title';
import type { AppSidebarConfig } from './chat-config';
import { CHAT_FORM_MOTION_MS, SHARED_CANVAS_CHAT_PROMPT_NAME } from './const';
import { confirmAndDeleteChat } from './delete-chat';
import { readLastOpenChat, rememberLastOpenChat } from './last-open-chat';
import {
  createChatPanelDocPath,
  normalizeChatPanelSessionId,
  takeChatSessionIdFromUrl,
} from './navigation';

/**
 * sl-uq2j: Context-dependent behavior for ChatPanel.
 * Determines how source navigation and other context-specific actions work.
 */
export interface ChatViewContext {
  type: 'doc' | 'graph-explorer' | 'workspace' | 'app-base-design';
  /** Current active document ID (undefined on non-doc pages) */
  docId?: string;
  /** Custom handler for source button clicks (e.g. graph node highlighting) */
  onSourceClick?: (docId: string, blockIds?: string[]) => void;
  /** Structured context supplied by non-doc surfaces such as AppBase Design. */
  payload?: unknown;
  /** Extra managed-AI context documents appended through the existing chat pipeline. */
  contextDocs?: ExternalChatContextDoc[];
}

export class ChatPanel extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    chat-panel {
      width: 100%;
      user-select: none;

      .chat-panel-container {
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      .canvas-chat-session-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin: 0 12px 8px;
        padding: 8px 10px;
        border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
        border-radius: 8px;
        color: var(--streamlain-v2-text-secondary);
        font-size: var(--streamlain-font-xs);
      }

      .canvas-chat-session-banner button {
        border: 0;
        padding: 0;
        background: transparent;
        color: var(--streamlain-v2-brand-primary);
        cursor: pointer;
        font: inherit;
        white-space: nowrap;
      }

      ai-chat-content {
        height: 0;
        flex-grow: 1;
      }

      /* Without its history the chat is as tall as what is left of it. */
      &[data-lean] ai-chat-content,
      &[data-minimized] ai-chat-content {
        height: auto;
        flex-grow: 0;
      }

      .chat-loading-container {
        position: relative;
        padding: 44px 0 166px 0;
        height: 100%;
        display: flex;
        align-items: center;
      }

      .chat-loading {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
      }

      .chat-loading-title {
        font-weight: 600;
        font-size: var(--streamlain-font-sm);
        color: var(--streamlain-v2-text-secondary);
      }

      textarea,
      input,
      [contenteditable='true'],
      chat-content-pure-text,
      chat-content-rich-text,
      chat-content-rich-text *,
      chat-panel-user-message .text-content-wrapper {
        user-select: text;
      }

      button,
      [role='button'],
      chat-panel-chip {
        user-select: none;
      }
    }
  `;

  /**
   * sl-uq2j: workspaceId is the primary scope — always required.
   * doc and host are optional (only present on doc pages with an editor).
   */
  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor host: EditorHost | undefined;

  @property({ attribute: false })
  accessor doc: Store | undefined;

  @property({ attribute: false })
  accessor playgroundConfig!: AIPlaygroundConfig;

  @property({ attribute: false })
  accessor appSidebarConfig!: AppSidebarConfig;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor reasoningConfig!: AIReasoningConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor extensions!: ExtensionType[];

  @property({ attribute: false })
  accessor serverService!: ServerService;

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor affineWorkspaceDialogService!: WorkspaceDialogService;

  @property({ attribute: false })
  accessor affineWorkbenchService!: WorkbenchService;

  @property({ attribute: false })
  accessor affineThemeService!: AppThemeService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor aiDraftService!: AIDraftService;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  @property({ attribute: false })
  accessor subscriptionService!: SubscriptionService;

  @property({ attribute: false })
  accessor workspaceDocSyncEngine: WorkspaceDocSyncEngine | null | undefined;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor onAISubscribe!: () => Promise<void>;

  @property({ attribute: false })
  accessor targetModeEnabled = false;

  // i18n strings for "No Keys Configured" card
  @property({ attribute: false })
  accessor noKeysTitle: string | undefined;

  @property({ attribute: false })
  accessor noKeysDescription: string | undefined;

  @property({ attribute: false })
  accessor noKeysButtonText: string | undefined;

  @property({ attribute: false })
  accessor mates: MateOptionData[] = [];

  @property({ attribute: false })
  accessor selectedMateId: string | null = null;

  @property({ attribute: false })
  accessor onSelectMate: ((mateId: string) => void) | undefined;

  @property({ attribute: false })
  accessor viewContext: ChatViewContext | undefined;

  @property({ attribute: false })
  accessor workspaceViewContextReader: WorkspaceViewContextReader | undefined;

  @property({ attribute: false })
  accessor workspaceNavigationPort: WorkspaceNavigationPort | undefined;

  @property({ attribute: false })
  accessor contextPickingControl: ChatContextPickingControl | undefined;

  /** Active workflow-builder context, independent from document host/doc state. */
  @property({ attribute: false })
  accessor activeWorkflowAttachment: WorkflowContextAttachment | null = null;

  /** Incremented for explicit "Edit with AI" requests after manual dismissal. */
  @property({ attribute: false })
  accessor activeWorkflowContextForce = 0;

  @property({ attribute: false })
  accessor onAssistantMessage:
    | ((message: ChatMessage, context: ChatViewContext | undefined) => void)
    | undefined;

  @state()
  accessor session: CopilotChatHistoryFragment | null | undefined;

  /**
   * sl-uq2j: Stable content key — only incremented on explicit session switches
   * (newSession, openSession), NOT on createSession (first message).
   * Prevents keyed() from destroying ai-chat-content during session creation.
   */
  @state()
  accessor _contentKey: number = 0;

  @state()
  accessor canvasSessionActive = false;

  /**
   * The conversation of the chat block chosen on the canvas. While it is set
   * the chat is lean: its title and its input, the answers land in the block.
   */
  @property({ attribute: false })
  accessor blockTurns: ChatBlockTurns | null = null;

  /**
   * Minimized, the chat is its title and its input: the history is put away.
   * It is so once the assistant operates the view; the user may change it.
   */
  @property({ type: Boolean })
  accessor minimized = false;

  /**
   * A surface's Mate button asks in a thread of its own, the history put
   * away so the surface stays in view above the chat. Resolved once the new
   * thread's content (and its input) is in place.
   */
  async startAssistThread(): Promise<void> {
    this.newSession();
    this.assistThread = true;
    this.minimized = true;
    await this.updateComplete;
  }

  /**
   * The thread a Mate button opened: its answers leave the history put away,
   * so the surface Mate fills stays in view; its line over the input opens it.
   */
  @state()
  accessor assistThread = false;

  /** Puts the shown history away; resolved once the window has followed. */
  async minimize(): Promise<void> {
    if (this.minimized || this.blockTurns !== null) return;
    if (!this.isConnected || !this.isSidebarOpen.value) return;
    this.minimized = true;
    await this.updateComplete;
    await new Promise(resolve => setTimeout(resolve, CHAT_FORM_MOTION_MS));
  }

  private independentSessionBeforeCanvas:
    | CopilotChatHistoryFragment
    | null
    | undefined;

  private lastAssistantMessageId: string | null = null;

  private isSidebarOpen: Signal<boolean | undefined> = signal(false);

  private get isInitialized() {
    return this.session !== undefined;
  }

  private readonly getSessionIdFromUrl = () =>
    this.affineWorkbenchService
      ? takeChatSessionIdFromUrl(this.affineWorkbenchService.workbench)
      : undefined;

  private readonly setSession = (
    session: CopilotChatHistoryFragment | null | undefined
  ) => {
    this.session = session ?? null;
  };

  private readonly initSession = async () => {
    if (!AIProvider.session || !this.workspaceId) {
      return;
    }
    // The chat named by the link, else the chat that was open last here.
    const sessionId =
      this.getSessionIdFromUrl() || readLastOpenChat(this.workspaceId);
    const wanted = sessionId
      ? await AIProvider.session.getSession(this.workspaceId, sessionId)
      : undefined;
    if (wanted) {
      this.setSession(wanted);
      return;
    }

    // sl-uq2j: latest workspace session (no docId filter)
    const sessions = await AIProvider.session.getSessions(
      this.workspaceId,
      undefined,
      {
        action: false,
        fork: false,
        excludePromptNames: [SHARED_CANVAS_CHAT_PROMPT_NAME],
      },
      { first: 1 }
    );
    // sessions is descending ordered by updatedAt
    // the first item is the latest session
    this.setSession(sessions?.[0]);
  };

  private readonly createSession = async (
    options: Partial<BlockSuitePresets.AICreateSessionOptions> = {}
  ) => {
    if (this.session) {
      return this.session;
    }
    // sl-uq2j: workspace-scoped session (no docId)
    const sessionId = await AIProvider.session?.createSession({
      workspaceId: this.workspaceId,
      promptName: `Chat With ${BRAND_DISPLAY_NAME} AI`,
      reuseLatestChat: false,
      ...options,
    });
    if (sessionId) {
      const session = await AIProvider.session?.getSession(
        this.workspaceId,
        sessionId
      );
      this.setSession(session);
    }
    return this.session;
  };

  private readonly deleteSession = async (
    session: BlockSuitePresets.AIRecentSession
  ) => {
    if (!(await confirmAndDeleteChat(this.notificationService, session))) {
      return false;
    }
    if (session.sessionId === this.session?.sessionId) {
      this.newSession();
    }
    return true;
  };

  private readonly updateSession = async (
    options: Omit<UpdateChatSessionInput, 'workspaceId'>
  ) => {
    await AIProvider.session?.updateSession({
      ...options,
      workspaceId: this.workspaceId,
    });
    // The history changes any chat; only a change of the open one shows here.
    if (this.session?.sessionId !== options.sessionId) return;
    const session = await AIProvider.session?.getSession(
      this.workspaceId,
      options.sessionId
    );
    if (this.session?.sessionId === options.sessionId) {
      this.setSession(session);
    }
  };

  private readonly newSession = () => {
    rememberLastOpenChat(this.workspaceId, null);
    this.canvasSessionActive = false;
    this.independentSessionBeforeCanvas = undefined;
    this.resetPanel();
    this._contentKey++;
    requestAnimationFrame(() => {
      this.session = null;
    });
  };

  private readonly openSession = async (sessionId: string) => {
    if (this.session?.sessionId === sessionId) {
      return;
    }
    this.resetPanel();
    this._contentKey++;
    const session = await AIProvider.session?.getSession(
      this.workspaceId,
      sessionId
    );
    this.canvasSessionActive = false;
    this.independentSessionBeforeCanvas = undefined;
    this.setSession(session);
  };

  openCanvasSession = async (sessionId: string) => {
    if (!sessionId || !AIProvider.session) return false;
    if (this.canvasSessionActive && this.session?.sessionId === sessionId) {
      return true;
    }

    const session = await AIProvider.session.getSession(
      this.workspaceId,
      sessionId
    );
    if (!session) return false;

    if (!this.canvasSessionActive) {
      this.independentSessionBeforeCanvas = this.session ?? null;
    }
    this.resetPanel();
    this._contentKey++;
    this.canvasSessionActive = true;
    this.setSession(session);
    return true;
  };

  private readonly returnToIndependentSession = () => {
    const session = this.independentSessionBeforeCanvas ?? null;
    this.resetPanel();
    this._contentKey++;
    this.canvasSessionActive = false;
    this.independentSessionBeforeCanvas = undefined;
    this.setSession(session);
  };

  private readonly openDoc = async (docId: string, sessionId?: string) => {
    const normalizedSessionId = normalizeChatPanelSessionId(sessionId);
    if (this.doc?.id === docId) {
      if (
        !normalizedSessionId ||
        this.session?.sessionId === normalizedSessionId
      ) {
        return;
      }
      await this.openSession(normalizedSessionId);
    } else if (this.affineWorkbenchService) {
      const { workbench } = this.affineWorkbenchService;
      workbench.open(createChatPanelDocPath(docId, normalizedSessionId), {
        at: 'active',
      });
    }
  };

  private readonly bindTelegramThread = async () => {
    try {
      const session = await this.createSession();
      if (!session?.sessionId) {
        notify.error({
          title: I18n.t('com.streamlain.ai.chat.telegram.no-session'),
        });
        return;
      }
      await workspaceApiFetch(
        `/api/workspace/${encodeURIComponent(this.workspaceId)}/assistant/telegram/me/thread`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: session.sessionId }),
        }
      );
      notify.success({
        title: I18n.t('com.streamlain.ai.chat.telegram.bound-to-chat'),
      });
    } catch (error) {
      // The server answers in English; the user reads the translated text.
      notify.error({
        title: I18n.t(
          error instanceof WorkspaceApiError && error.status === 400
            ? 'com.streamlain.ai.chat.telegram.not-connected'
            : 'com.streamlain.ai.chat.telegram.bind-failed'
        ),
      });
    }
  };

  private readonly initPanel = async () => {
    try {
      if (!this.isSidebarOpen.value) {
        return;
      }
      await this.initSession();
    } catch (error) {
      if (!isBackendUnavailableError(error)) {
        console.error(error);
      }
      // Local-first: remote history is optional for opening the sidebar. If the
      // backend/network is unavailable, show an empty chat instead of keeping
      // the panel in the loading state forever.
      this.setSession(null);
    }
  };

  private readonly resetPanel = () => {
    this.session = undefined;
    this.assistThread = false;
  };

  // sl-8m4o.1: Navigate to workspace AI settings (not individual AI settings)
  private readonly openAISettings = () => {
    this.affineWorkspaceDialogService?.open('setting', {
      activeTab: 'workspace:ai',
    });
  };

  private readonly onContextChange = async (
    context: Partial<ChatContextValue>
  ) => {
    const assistant = context.messages
      ?.filter(
        (message): message is ChatMessage =>
          'role' in message && message.role === 'assistant'
      )
      .at(-1);
    if (
      assistant &&
      assistant.id !== this.lastAssistantMessageId &&
      assistant.content.trim()
    ) {
      this.lastAssistantMessageId = assistant.id;
      this.onAssistantMessage?.(assistant, this.viewContext);
    }
  };

  override connectedCallback() {
    super.connectedCallback();

    this._disposables.add(
      AIProvider.slots.userInfo.subscribe(() => {
        this.resetPanel();
        this.initPanel().catch(console.error);
      })
    );

    // sl-uq2j: Re-bind sidebar config (disposables were cleaned on disconnect)
    this._sidebarConfigBound = false;
    this._bindSidebarConfigIfReady();
  }

  override disconnectedCallback() {
    // sl-uq2j: Mark as unbound so next connectedCallback re-binds.
    // WithDisposable mixin will clean up _disposables automatically.
    this._sidebarConfigBound = false;
    super.disconnectedCallback();
  }

  /**
   * sl-uq2j: Bind sidebar config signals. Safe to call multiple times —
   * only binds once when appSidebarConfig is available.
   */
  private _sidebarConfigBound = false;
  private _bindSidebarConfigIfReady() {
    if (this._sidebarConfigBound || !this.appSidebarConfig) return;
    this._sidebarConfigBound = true;

    const isOpen = this.appSidebarConfig.isOpen();
    this.isSidebarOpen = isOpen.signal;
    this._disposables.add(isOpen.cleanup);

    this._disposables.add(
      this.isSidebarOpen.subscribe(isOpen => {
        // sl-uq2j: Only init if not already initialized (survives re-mount)
        if (isOpen && !this.isInitialized) {
          this.initPanel().catch(console.error);
        }
      })
    );
  }

  protected override willUpdate(changedProperties: PropertyValues) {
    // The host measures the chat in its old form before this update renders the new one.
    if (
      changedProperties.has('blockTurns') ||
      changedProperties.has('minimized')
    )
      this.dispatchEvent(new CustomEvent('lean-change'));
  }

  protected override updated(changedProperties: PropertyValues) {
    // sl-uq2j: Try to bind sidebar config after properties are set
    if (changedProperties.has('appSidebarConfig')) {
      this._bindSidebarConfigIfReady();
    }
    // The chat of a chat block is the block's own; it is not the chat to
    // come back to.
    if (
      changedProperties.has('session') &&
      this.session &&
      !this.canvasSessionActive
    ) {
      rememberLastOpenChat(this.workspaceId, this.session.sessionId);
    }
    if (
      changedProperties.has('blockTurns') ||
      changedProperties.has('minimized')
    ) {
      this.toggleAttribute('data-lean', this.blockTurns !== null);
      this.toggleAttribute('data-minimized', this.minimized);
    }
  }

  override render() {
    if (!this.isInitialized) {
      return html`<div class="chat-loading-container">
        <div class="chat-loading">
          ${AffineIcon('var(--streamlain-v2-icon-secondary)')}
          <div class="chat-loading-title">
            <span>${I18n.t('com.streamlain.ai.chat.loading-history')}</span>
          </div>
        </div>
      </div>`;
    }

    const lean = this.blockTurns !== null;
    return html`<div class="chat-panel-container" data-copilot-ignore>
      <ai-chat-panel-title
        .lean=${lean}
        .workspaceId=${this.workspaceId}
        .host=${this.host}
        .doc=${this.doc}
        .playgroundConfig=${this.playgroundConfig}
        .appSidebarConfig=${this.appSidebarConfig}
        .networkSearchConfig=${this.networkSearchConfig}
        .reasoningConfig=${this.reasoningConfig}
        .aiModelService=${this.aiModelService}
        .searchMenuConfig=${this.searchMenuConfig}
        .docDisplayConfig=${this.docDisplayConfig}
        .extensions=${this.extensions}
        .serverService=${this.serverService}
        .affineFeatureFlagService=${this.affineFeatureFlagService}
        .affineWorkspaceDialogService=${this.affineWorkspaceDialogService}
        .affineThemeService=${this.affineThemeService}
        .notificationService=${this.notificationService}
        .aiToolsConfigService=${this.aiToolsConfigService}
        .session=${this.session}
        .newSession=${this.newSession}
        .openSession=${this.openSession}
        .openDoc=${this.openDoc}
        .deleteSession=${this.deleteSession}
        .updateSession=${this.updateSession}
        .mates=${this.mates}
        .selectedMateId=${this.selectedMateId}
        .onSelectMate=${this.onSelectMate}
        .onBindTelegramThread=${this.bindTelegramThread}
      ></ai-chat-panel-title>
      ${this.canvasSessionActive && !lean
        ? html`<div
            class="canvas-chat-session-banner"
            data-testid="canvas-chat-session-banner"
          >
            <span
              >${I18n.t(
                'com.streamlain.ai.chat.canvas.sidebar-session-label'
              )}</span
            >
            <button
              type="button"
              data-testid="return-to-sidebar-chat"
              @click=${this.returnToIndependentSession}
            >
              ${I18n.t('com.streamlain.ai.chat.canvas.return-to-sidebar-chat')}
            </button>
          </div>`
        : null}
      ${keyed(
        // sl-uq2j: Key by _contentKey — only changes on explicit session switch,
        // NOT on createSession (preserves first message display)
        this._contentKey,
        html`<ai-chat-content
          .canvasAIChatContextMode=${this.canvasSessionActive
            ? 'lineage-only'
            : 'selection-analysis'}
          .host=${this.host}
          .blockTurns=${this.blockTurns}
          .minimized=${this.minimized}
          .keepHistoryAway=${this.assistThread}
          @history-needed=${() => (this.minimized = false)}
          .session=${this.session}
          .createSession=${this.createSession}
          .workspaceViewContextReader=${this.workspaceViewContextReader}
          .workspaceNavigationPort=${this.workspaceNavigationPort}
          .contextPickingControl=${this.contextPickingControl}
          .workspaceId=${this.workspaceId}
          .docId=${this.doc?.id}
          .networkSearchConfig=${this.networkSearchConfig}
          .reasoningConfig=${this.reasoningConfig}
          .searchMenuConfig=${this.searchMenuConfig}
          .docDisplayConfig=${this.docDisplayConfig}
          .extensions=${this.extensions}
          .serverService=${this.serverService}
          .affineFeatureFlagService=${this.affineFeatureFlagService}
          .affineWorkspaceDialogService=${this.affineWorkspaceDialogService}
          .affineThemeService=${this.affineThemeService}
          .notificationService=${this.notificationService}
          .aiDraftService=${this.aiDraftService}
          .aiToolsConfigService=${this.aiToolsConfigService}
          .peekViewService=${this.peekViewService}
          .subscriptionService=${this.subscriptionService}
          .workspaceDocSyncEngine=${this.workspaceDocSyncEngine}
          .aiModelService=${this.aiModelService}
          .onAISubscribe=${this.onAISubscribe}
          .targetModeEnabled=${this.targetModeEnabled}
          .onContextChange=${this.onContextChange}
          .activeWorkflowAttachment=${this.activeWorkflowAttachment}
          .activeWorkflowContextForce=${this.activeWorkflowContextForce}
          .externalContextDocs=${this.viewContext?.contextDocs ?? []}
          .onOpenDoc=${this.openDoc}
          .onOpenSettings=${this.openAISettings}
          .noKeysTitle=${this.noKeysTitle}
          .noKeysDescription=${this.noKeysDescription}
          .noKeysButtonText=${this.noKeysButtonText}
          .mates=${this.mates}
          .onSelectMate=${this.onSelectMate}
        ></ai-chat-content>`
      )}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-panel': ChatPanel;
  }
}
